import { Instant } from '@js-joda/core';
import type { AnalystRating } from '@thinkalpha/quant-ws-client/contracts/analyst-ratings.js';

/**
 * Half-life of a rating's contribution to its firm's recency score. A rating
 * 60 days old contributes 50% of a fresh one; 180 days old contributes ~12.5%.
 */
export const FIRM_RECENCY_HALF_LIFE_DAYS = 60;

const LN2 = Math.log(2);
const MS_PER_DAY = 86_400_000;

/**
 * Rank firms by recency-weighted rating count and return the top `limit`
 * names. Exponential decay biases toward firms publishing recently while still
 * surfacing prolific veterans.
 */
export function computeTopFirms(
    items: readonly AnalystRating[],
    limit = 8,
    now: Instant = Instant.now(),
    halfLifeDays = FIRM_RECENCY_HALF_LIFE_DAYS,
): string[] {
    if (items.length === 0 || limit <= 0) return [];
    const effectiveHalfLifeDays = halfLifeDays > 0 ? halfLifeDays : FIRM_RECENCY_HALF_LIFE_DAYS;
    const nowMs = now.toEpochMilli();
    const score = new Map<string, number>();
    for (const r of items) {
        const ageDays = Math.max(0, (nowMs - r.publishedAt.toEpochMilli()) / MS_PER_DAY);
        const w = Math.exp((-LN2 * ageDays) / effectiveHalfLifeDays);
        score.set(r.firm, (score.get(r.firm) ?? 0) + w);
    }
    return score
        .entries()
        .map(([firm, s]) => ({ firm, s }))
        .toArray()
        .sort((a, b) => b.s - a.s || a.firm.localeCompare(b.firm))
        .slice(0, limit)
        .map((e) => e.firm);
}
